<template>
  <div class="admin-dashboard">
    <div class="charts-grid">
      <!-- 访问趋势图 -->
      <div class="chart-card">
        <div class="chart-container">
          <div id="access-trend-chart" style="height: 400px;"></div>
        </div>
      </div>

      <!-- 页面统计和地域分布 -->
      <div class="half-width-cards">
        <div class="chart-card">
          <div class="chart-container">
            <div id="page-stats-chart" style="height: 350px;"></div>
          </div>
        </div>

        <div class="chart-card">
          <div class="chart-container">
            <div id="location-stats-chart" style="height: 350px;"></div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <span>数据加载中...</span>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import { onMounted, onUnmounted, ref, nextTick } from 'vue';
import request from '@/utils/request'

export default {
  name: "AdminDashboard",
  setup() {
    const loading = ref(true);
    const chartInstances = ref([]);

    onMounted(() => {
      initCharts();
    });

    onUnmounted(() => {
      chartInstances.value.forEach(chart => {
        if (chart) {
          chart.dispose();
        }
      });
    });

    const initCharts = async () => {
      try {
        await Promise.all([
          initAccessTrendChart(),
          initPageStatsChart(),
          initLocationStatsChart()
        ]);
      } catch (error) {
        console.error('初始化图表失败:', error);
      } finally {
        loading.value = false;
      }
    };

    const initAccessTrendChart = async () => {
      try {
        const result = await request.get('/log/access-trend', {
          params: { days: 30 }
        });

        if (result.code === '200') {
          const data = result.data;
          const chartDom = document.getElementById('access-trend-chart');
          if (!chartDom) return;

          const chart = echarts.init(chartDom);
          chartInstances.value.push(chart);

          const chartData = data.dates.map((date, index) => {
            const timestamp = new Date(date).getTime();
            return [timestamp, data.accessCounts[index]];
          });

          const option = {
            tooltip: {
              trigger: 'axis',
              position: function (pt) {
                return [pt[0], '10%'];
              },
              backgroundColor: 'rgba(255,255,255,0.95)',
              borderColor: '#dcdfe6',
              borderWidth: 1,
              textStyle: {
                color: '#606266'
              },
              formatter: function(params) {
                const date = new Date(params[0].value[0]);
                const year = date.getFullYear();
                const month = (date.getMonth() + 1).toString().padStart(2, '0');
                const day = date.getDate().toString().padStart(2, '0');
                const formattedDate = `${year}-${month}-${day}`;
                return `${formattedDate}<br/>访问量: ${params[0].value[1]}`;
              }
            },
            title: {
              left: 'center',
              text: '访问趋势统计'
            },
            toolbox: {
              feature: {
                dataZoom: {
                  yAxisIndex: 'none'
                },
                restore: {},
                saveAsImage: {}
              }
            },
            xAxis: {
              type: 'time',
              boundaryGap: false,
              axisLine: {
                lineStyle: {
                  color: '#dcdfe6'
                }
              },
              axisLabel: {
                color: '#606266',
                interval: 0,
                margin: 10,
                formatter: function(value) {
                  const date = new Date(value);
                  return (date.getMonth() + 1) + '-' + date.getDate();
                }
              },
              splitLine: {
                show: true,
                lineStyle: {
                  color: '#f0f2f5',
                  type: 'dashed'
                }
              }
            },
            yAxis: {
              type: 'value',
              boundaryGap: [0, '100%'],
              axisLine: {
                lineStyle: {
                  color: '#dcdfe6'
                }
              },
              axisLabel: {
                color: '#606266'
              },
              splitLine: {
                lineStyle: {
                  color: '#f0f2f5',
                  type: 'dashed'
                }
              }
            },
            dataZoom: [
              {
                type: 'inside',
                start: 10,
                end: 30,
                xAxisIndex: 0
              },
              {
                type: 'slider',
                show: true,
                xAxisIndex: 0,
                left: '3%',
                right: '4%',
                bottom: '3%',
                height: 20,
                handleSize: 8,
                handleStyle: {
                  color: '#5470c6'
                },
                textStyle: {
                  color: '#606266'
                },
                fillerColor: 'rgba(84, 112, 198, 0.2)',
                borderColor: '#dcdfe6',
                labelFormatter: function(value) {
                  const date = new Date(value);
                  const year = date.getFullYear();
                  const month = (date.getMonth() + 1).toString().padStart(2, '0');
                  const day = date.getDate().toString().padStart(2, '0');
                  return `${year}-${month}-${day}`;
                }
              }
            ],
            series: [
              {
                name: '访问量',
                type: 'line',
                smooth: true,
                symbol: 'none',
                sampling: 'lttb',
                itemStyle: {
                  color: '#5470c6'
                },
                areaStyle: {
                  color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                    {
                      offset: 0,
                      color: 'rgba(84, 112, 198, 0.5)'
                    },
                    {
                      offset: 1,
                      color: 'rgba(84, 112, 198, 0.1)'
                    }
                  ])
                },
                data: chartData,
                z: 2
              }
            ],
            grid: {
              left: '3%',
              right: '4%',
              bottom: '12%',
              top: '15%',
              containLabel: true
            }
          };

          chart.setOption(option);

          const resizeHandler = () => chart.resize();
          window.addEventListener('resize', resizeHandler);
          chart._resizeHandler = resizeHandler;
        }
      } catch (error) {
        console.error('初始化访问趋势图表失败:', error);
      }
    };

    const initPageStatsChart = async () => {
      try {
        const result = await request.get('/log/page-stats', {
          params: { limit: 8 }
        });

        if (result.code === '200') {
          const data = result.data;
          const chartDom = document.getElementById('page-stats-chart');
          if (!chartDom) return;

          const chart = echarts.init(chartDom);
          chartInstances.value.push(chart);

          const pageColors = [
            '#5470c6', '#91cc75', '#fac858', '#ee6666',
            '#73c0de', '#3ba272', '#fc8452', '#9a60b4'
          ];

          const fixedCategories = ['其他页面', '首页', '产品页', '定价页'];

          const categoryMap = {};
          data.forEach(item => {
            categoryMap[item.page] = item.count;
          });

          const processedData = fixedCategories.map((category, index) => {
            return {
              page: category,
              count: categoryMap[category] || 0,
              color: pageColors[index % pageColors.length]
            };
          });

          const option = {
            title: {
              text: '近七天热门页面访问量',
              left: 'center',
              textStyle: {
                color: '#2c3e50',
                fontSize: 16,
                fontWeight: 'bold'
              }
            },
            tooltip: {
              trigger: 'axis',
              axisPointer: {
                type: 'shadow'
              },
              formatter: function(params) {
                const data = params[0];
                return `${data.name}<br/>访问次数: ${data.value}`;
              }
            },
            grid: {
              left: '3%',
              right: '4%',
              bottom: '8%',
              top: '15%',
              containLabel: true
            },
            xAxis: {
              type: 'value',
              axisLine: {
                lineStyle: {
                  color: '#dcdfe6'
                }
              },
              axisLabel: {
                color: '#606266',
                margin: 8,
                formatter: function(value) {
                  if (value >= 1000) {
                    return (value / 1000).toFixed(1) + 'k';
                  }
                  return value;
                }
              },
              name: '访问次数',
              nameTextStyle: {
                color: '#606266',
                fontSize: 12,
                padding: [10, 0, 0, 0]
              },
              nameLocation: 'middle',
              nameGap: 25
            },
            yAxis: {
              type: 'category',
              data: processedData.map(item => item.page),
              axisLine: {
                lineStyle: {
                  color: '#dcdfe6'
                }
              },
              axisLabel: {
                color: '#606266',
                margin: 8,
                formatter: (value) => {
                  if (value.length > 8) {
                    return value.substring(0, 8) + '...';
                  }
                  return value;
                }
              }
            },
            series: [{
              name: '访问次数',
              type: 'bar',
              data: processedData.map((item, index) => ({
                value: item.count,
                itemStyle: {
                  color: item.color
                }
              })),
              emphasis: {
                itemStyle: {
                  color: function(params) {
                    const color = processedData[params.dataIndex].color;
                    return echarts.color.lift(color, -0.2);
                  }
                }
              },
              label: {
                show: true,
                position: 'right',
                color: '#606266',
                formatter: '{c}次',
                fontSize: 12
              }
            }]
          };

          chart.setOption(option);

          const resizeHandler = () => chart.resize();
          window.addEventListener('resize', resizeHandler);
          chart._resizeHandler = resizeHandler;
        }
      } catch (error) {
        console.error('初始化页面统计图表失败:', error);
      }
    };

    const initLocationStatsChart = async () => {
      try {
        const result = await request.get('/log/location-stats', {
          params: { limit: 10 }
        });

        if (result.code === '200') {
          const data = result.data;
          const chartDom = document.getElementById('location-stats-chart');
          if (!chartDom) return;

          const chart = echarts.init(chartDom);
          chartInstances.value.push(chart);

          const option = {
            title: {
              text: '地域访问分布',
              left: 'center',
              textStyle: {
                color: '#2c3e50',
                fontSize: 16,
                fontWeight: 'bold'
              }
            },
            tooltip: {
              trigger: 'item',
              formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: {
              orient: 'vertical',
              left: 'left',
              top: 'middle',
              textStyle: {
                color: '#606266'
              }
            },
            series: [{
              name: '访问来源',
              type: 'pie',
              radius: ['40%', '70%'],
              avoidLabelOverlap: false,
              itemStyle: {
                borderRadius: 10,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: {
                show: false,
                position: 'center'
              },
              emphasis: {
                label: {
                  show: true,
                  fontSize: 18,
                  fontWeight: 'bold'
                }
              },
              labelLine: {
                show: false
              },
              data: data.map(item => ({
                value: item.count,
                name: item.location
              }))
            }]
          };

          chart.setOption(option);

          const resizeHandler = () => chart.resize();
          window.addEventListener('resize', resizeHandler);
          chart._resizeHandler = resizeHandler;
        }
      } catch (error) {
        console.error('初始化地域统计图表失败:', error);
      }
    };

    return {
      loading
    };
  }
}
</script>

<style scoped>
.admin-dashboard {
  padding: 20px;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.charts-grid {
  display: flex;
  flex-direction: column;
  gap: 20px;

}

.chart-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  transition: all 0.3s ease;
}

.half-width-cards {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.chart-container {
  position: relative;
  width: 100%;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #5470c6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 响应式设计 */
@media (max-width: 768px) {
  .admin-dashboard {
    padding: 16px;
  }

  .half-width-cards {
    grid-template-columns: 1fr;
  }

  .chart-card {
    padding: 16px;
  }
}
</style>